import { apiSlice, TagTypes } from '@/redux/slices/apiSlice';
import {
  AuthorizationUrlResponse,
  getAppIntegrationResourceArgs,
  IntegrationSupportResponse,
  IntegrationTokenScopes,
  ResourceResponse,
  CommonIntegrationArgs,
  CreateOAuthConfigurationArgs,
  UpdateOAuthConfigurationArgs,
  toIntegrationTokenScopes,
} from '@/types/Integrations';
import {
  EntitlementSlugEnum,
  GetAuthorizationUrlResponse,
  GetIntegrationSupportResponse,
  IntegrationHealthCheckResponse,
  IntegrationUsage,
  NangoConnectionResponse,
  NangoConnectionTokenResponse,
  ResourceResponse as ResourceResponseDto,
  OAuthConfigurationResponse,
  TokenScopesResponse,
} from '@electricjs/core_entity-client';
import { RequestTypeSetting } from '@/types/requests';

export const integrationsSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getAuthorizationUrl: builder.query<
      AuthorizationUrlResponse,
      {
        organizationId: string;
        applicationKey: string;
        redirectUri: string;
      }
    >({
      query: ({ organizationId, applicationKey, redirectUri }) => {
        return {
          url: `/v1/integrations/oauth/get-authorization-url`,
          params: {
            organization_id: organizationId,
            application_key: applicationKey,
            redirect_uri: redirectUri,
          },
        };
      },
      transformResponse: (
        response: GetAuthorizationUrlResponse
      ): AuthorizationUrlResponse => {
        return { authorizationUrl: response.authorization_url };
      },
    }),
    healthCheck: builder.query<
      IntegrationHealthCheckResponse,
      CommonIntegrationArgs
    >({
      query: ({ organizationId, applicationKey }) => {
        return {
          url: `/v1/organizations/${organizationId}/applications/${applicationKey}/integration/health`,
        };
      },
    }),
    getOAuthCallback: builder.query<
      IntegrationHealthCheckResponse,
      {
        organizationId: string;
        code: string;
        state: string;
      }
    >({
      query: ({ organizationId, code, state }) => {
        return {
          url: `/v1/integrations/oauth/callback`,
          params: {
            organization_id: organizationId,
            code,
            state,
          },
        };
      },
    }),
    getMicrosoftOAuthCallback: builder.query<
      void,
      {
        organizationId: string;
        tenant: string;
        state: string;
      }
    >({
      query: ({ organizationId, tenant, state }) => {
        return {
          url: `/v1/integrations/oauth/microsoft-callback`,
          params: {
            organization_id: organizationId,
            tenant,
            state,
          },
        };
      },
    }),
    getMicrosoftIntuneOAuthCallback: builder.query<
      void,
      {
        organizationId: string;
        tenant: string;
        state: string;
      }
    >({
      query: ({ organizationId, tenant, state }) => {
        return {
          url: `/v1/integrations/oauth/microsoft-intune/callback`,
          params: {
            organization_id: organizationId,
            tenant,
            state,
          },
        };
      },
    }),
    getMicrosoftActiveDirectoryOAuthCallback: builder.query<
      void,
      {
        organizationId: string;
        tenant: string;
        state: string;
      }
    >({
      query: ({ organizationId, tenant, state }) => {
        return {
          url: `/v1/integrations/oauth/microsoft-on-prem-active-directory/callback`,
          params: {
            organization_id: organizationId,
            tenant,
            state,
          },
        };
      },
    }),
    getGitHubAppCallback: builder.query<
      IntegrationHealthCheckResponse,
      {
        organizationId: string;
        installationId: string;
        state: string;
      }
    >({
      query: ({ organizationId, installationId, state }) => {
        return {
          url: `/v1/integrations/oauth/github-callback`,
          params: {
            organization_id: organizationId,
            installation_id: installationId,
            state,
          },
        };
      },
    }),
    deleteApplicationIntegration: builder.mutation<
      void,
      {
        organizationId: string;
        applicationKey: string;
      }
    >({
      query: ({ organizationId, applicationKey }) => {
        return {
          url: `/v1/organizations/${organizationId}/applications/${applicationKey}/integration`,
          method: 'DELETE',
        };
      },
      invalidatesTags: [TagTypes.Application, TagTypes.IntegrationUsage],
    }),
    getIntegrationTokenScopes: builder.query<
      IntegrationTokenScopes,
      CommonIntegrationArgs
    >({
      query: ({ organizationId, applicationKey }) => {
        return {
          url: `/v1/organizations/${organizationId}/applications/${applicationKey}/integration/token-scopes`,
        };
      },
      transformResponse: (
        response: TokenScopesResponse
      ): IntegrationTokenScopes => toIntegrationTokenScopes(response),
    }),
    getIntegrationUsage: builder.query<
      { connectedCount: number; applicationsLimit?: number },
      string
    >({
      query: organizationId =>
        `/v1/organizations/${organizationId}/integrations/usage`,
      transformResponse: (response: IntegrationUsage) => ({
        connectedCount: response.connected_count,
        applicationsLimit: response.applications_limit,
      }),
      // Refetched whenever an integration is connected or disconnected (those
      // mutations invalidate IntegrationUsage), so the count stays in sync.
      providesTags: [TagTypes.IntegrationUsage],
    }),
    getIntegrationSupport: builder.query<
      IntegrationSupportResponse,
      CommonIntegrationArgs
    >({
      query: ({ organizationId, applicationKey }) => {
        return {
          url: `/v1/organizations/${organizationId}/applications/${applicationKey}/integration/support`,
          meta: {
            entitlements: [EntitlementSlugEnum.Integrations],
          },
        };
      },
      transformResponse: (
        response: GetIntegrationSupportResponse
      ): IntegrationSupportResponse => {
        return {
          operations: response.operations,
          planRestrictions: response.plan_restrictions ?? [],
          type: response.type,
        };
      },
    }),
    createNangoConnectionToken: builder.mutation<
      NangoConnectionTokenResponse,
      CommonIntegrationArgs
    >({
      invalidatesTags: [TagTypes.IntegrationUsage],
      query: ({ organizationId, applicationKey }) => {
        return {
          url: `/v1/organizations/${organizationId}/nango/connection-token`,
          method: 'POST',
          body: {
            application_key: applicationKey,
          },
        };
      },
    }),
    createNangoConnectionId: builder.mutation<
      NangoConnectionResponse,
      {
        organizationId: string;
        applicationKey: string;
        connectionId: string;
      }
    >({
      invalidatesTags: [TagTypes.Application, TagTypes.IntegrationUsage],
      query: ({ organizationId, applicationKey, connectionId }) => ({
        url: `/v1/organizations/${organizationId}/applications/${applicationKey}/integration/nango-connection`,
        method: 'POST',
        body: {
          connection_id: connectionId,
        },
      }),
    }),
    getAppIntegrationResource: builder.query<
      ResourceResponse,
      getAppIntegrationResourceArgs
    >({
      providesTags: [TagTypes.ApplicationResources],
      query: ({
        organizationId,
        applicationKey,
        resourceSlug,
        queryParams,
      }) => {
        return {
          url: `/v1/organizations/${organizationId}/applications/${applicationKey}/integration/resource/${resourceSlug}`,
          ...(queryParams ? { params: queryParams } : {}),
        };
      },
      transformResponse: (response: ResourceResponseDto) => {
        return {
          resourceLabel: response.resource_label as keyof RequestTypeSetting,
          resourceValue: response.resource_value as keyof RequestTypeSetting,
          result: response.resource,
          applicationId: response.application_id,
          error: response.error,
          resource: response.resource,
        };
      },
    }),
    createOAuthTokenForApplication: builder.mutation<
      IntegrationHealthCheckResponse,
      CommonIntegrationArgs
    >({
      invalidatesTags: [TagTypes.IntegrationUsage],
      query: ({ organizationId, applicationKey }) => {
        return {
          url: `/v1/organizations/${organizationId}/applications/${applicationKey}/oauth/token`,
          method: 'POST',
        };
      },
    }),
    createOAuthConfiguration: builder.mutation<
      OAuthConfigurationResponse,
      CreateOAuthConfigurationArgs
    >({
      invalidatesTags: [TagTypes.IntegrationUsage],
      query: ({
        organizationId,
        applicationKey,
        clientId,
        clientSecret,
        grantType,
        tokenUrl,
      }) => {
        return {
          url: `/v1/organizations/${organizationId}/applications/${applicationKey}/oauth/configuration`,
          method: 'POST',
          body: {
            client_id: clientId,
            client_secret: clientSecret,
            ...(tokenUrl && { token_url: tokenUrl }),
            grant_type: grantType,
          },
        };
      },
    }),
    getOAuthConfiguration: builder.query<
      OAuthConfigurationResponse,
      CommonIntegrationArgs
    >({
      query: ({ organizationId, applicationKey }) => {
        return {
          url: `/v1/organizations/${organizationId}/applications/${applicationKey}/oauth/configuration`,
        };
      },
    }),

    updateOAuthConfiguration: builder.mutation<
      OAuthConfigurationResponse,
      UpdateOAuthConfigurationArgs
    >({
      query: ({
        organizationId,
        applicationKey,
        clientId,
        clientSecret,
        grantType,
        tokenUrl,
      }) => {
        return {
          url: `/v1/organizations/${organizationId}/applications/${applicationKey}/oauth/configuration`,
          method: 'PUT',
          body: {
            ...(clientId && { client_id: clientId }),
            ...(clientSecret && { client_secret: clientSecret }),
            ...(tokenUrl && { token_url: tokenUrl }),
            ...(grantType && { grant_type: grantType }),
          },
        };
      },
    }),

    deleteOAuthConfiguration: builder.mutation<
      void,
      {
        organizationId: string;
        applicationKey: string;
      }
    >({
      invalidatesTags: [TagTypes.IntegrationUsage],
      query: ({ organizationId, applicationKey }) => {
        return {
          url: `/v1/organizations/${organizationId}/applications/${applicationKey}/oauth/configuration`,
          method: 'DELETE',
        };
      },
    }),

    getSupportedProfileFields: builder.query<string[], CommonIntegrationArgs>({
      query: ({ organizationId, applicationKey }) => ({
        url: `/v1/organizations/${organizationId}/applications/${applicationKey}/integration/supported-profile-fields`,
        meta: {
          entitlements: [EntitlementSlugEnum.Integrations],
        },
      }),
      transformResponse: (response: { fields: string[] }) => response.fields,
    }),

    batchHealthCheck: builder.query<
      Record<string, IntegrationHealthCheckResponse>,
      {
        organizationId: string;
        applicationKeys: string[];
      }
    >({
      queryFn: async (
        { organizationId, applicationKeys },
        _api,
        _extraOptions,
        baseQuery
      ) => {
        if (applicationKeys.length === 0) {
          return { data: {} };
        }

        const promises = applicationKeys.map(async appKey => {
          try {
            const result = await baseQuery({
              url: `/v1/organizations/${organizationId}/applications/${appKey}/integration/health`,
            });
            return { appKey, result };
          } catch (error) {
            return { appKey, error };
          }
        });

        const results = await Promise.allSettled(promises);
        const healthCheckMap: Record<string, IntegrationHealthCheckResponse> =
          {};

        results.forEach(result => {
          if (result.status === 'fulfilled') {
            const { appKey, result: healthCheckResult } = result.value;
            if (healthCheckResult?.data) {
              healthCheckMap[appKey] =
                healthCheckResult.data as IntegrationHealthCheckResponse;
            } else if (healthCheckResult?.error) {
              console.error(
                `Health check failed for ${appKey}:`,
                healthCheckResult.error
              );
            }
          } else {
            console.error('Promise rejected:', result.reason);
          }
        });

        return { data: healthCheckMap };
      },
    }),
  }),
});

// auto-generated hooks
export const {
  useCreateNangoConnectionIdMutation,
  useCreateOAuthTokenForApplicationMutation,
  useCreateOAuthConfigurationMutation,
  useUpdateOAuthConfigurationMutation,
  useGetOAuthConfigurationQuery,
  useLazyGetOAuthConfigurationQuery,
  useDeleteOAuthConfigurationMutation,
  useGetAuthorizationUrlQuery,
  useGetIntegrationUsageQuery,
  useGetIntegrationSupportQuery,
  useLazyGetIntegrationSupportQuery,
  useGetIntegrationTokenScopesQuery,
  useGetOAuthCallbackQuery,
  useGetMicrosoftOAuthCallbackQuery,
  useGetMicrosoftIntuneOAuthCallbackQuery,
  useGetMicrosoftActiveDirectoryOAuthCallbackQuery,
  useGetGitHubAppCallbackQuery,
  useDeleteApplicationIntegrationMutation,
  useCreateNangoConnectionTokenMutation,
  useGetAppIntegrationResourceQuery,
  useLazyGetAppIntegrationResourceQuery,
  useHealthCheckQuery,
  useBatchHealthCheckQuery,
  useGetSupportedProfileFieldsQuery,
} = integrationsSlice;
